"use client";

import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { LayoutGroup } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { LoaderCircle } from "lucide-react";

import { DiscCenterButton } from "@ui/components/core/Disc";
import { DiscButton } from "@ui/components/core/Disc/DiscButton";
import { cn, pxToRem } from "@ui/lib/utils";
import { sizes } from "@ui/lib/sizes";
import { formatTime } from "@ui/lib/time";
import { StopIcon } from "@ui/icons/Stop";
import Plus16 from "@ui/icons/16x16/plus-16.svg";
import Mic16 from "@ui/icons/16x16/mic-16.svg";
import { AUDIO_CONTENT_TYPES } from "@components/demos/STT/utils";

import imgRecordingBG from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 05.png";
import imgIsolatingBG from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 04.png";

import { MAX_UPLOAD_SIZE_BYTES, type UseVoiceIsolateReturn } from "./hooks/useVoiceIsolate";
import {
  CenteredLayout,
  FooterMessage,
  RecordingPrompts,
  SideBySideOmniboxLayout,
  ViewLayout,
} from "@ui/components/core/SideBySideOmnibox";
import IsolationResultView from "./IsolationResultView";

const PROMPT_CYCLE_INTERVAL = 5000;
const DISC_SIZES = sizes(pxToRem(128), { md: pxToRem(160) });

interface IsolateYourVoiceViewProps {
  voiceIsolateData: UseVoiceIsolateReturn;
}

export default function IsolateYourVoiceView({ voiceIsolateData }: IsolateYourVoiceViewProps) {
  const t = useTranslations("ClientKeys.VoiceIsolatorV2");
  const tPrompts = useTranslations("ClientKeys.VoiceIsolator.prompts");
  const [promptIndex, setPromptIndex] = useState(0);

  const prompts = useMemo(
    () => [
      { direction: tPrompts("1.direction"), text: tPrompts("1.text") },
      { direction: tPrompts("2.direction"), text: tPrompts("2.text") },
      { direction: tPrompts("3.direction"), text: tPrompts("3.text") },
      { direction: tPrompts("4.direction"), text: tPrompts("4.text") },
    ],
    [tPrompts],
  );

  const { step, duration, error, handleFileSelected, startRecording, stopRecording, setError } = voiceIsolateData;

  const {
    isDragActive,
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    multiple: false,
    accept: Object.fromEntries(AUDIO_CONTENT_TYPES.map(type => [type, []])),
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    onDropAccepted: files => handleFileSelected(files[0]),
    onDropRejected: rejected => {
      const { file, errors } = rejected[0];
      let errorMessage = t("error_file_generic", { fileName: file.name });
      if (errors.some(e => e.code === "file-invalid-type")) {
        errorMessage = t("error_file_invalid_type", { fileName: file.name });
      } else if (errors.some(e => e.code === "file-too-large")) {
        errorMessage = t("error_file_too_large", {
          fileName: file.name,
          maxSize: MAX_UPLOAD_SIZE_BYTES / (1024 * 1024),
        });
      }
      setError(errorMessage);
    },
  });

  const isRecording = step === "recording";

  // Cycle through prompts during recording
  useEffect(() => {
    if (!isRecording) {
      setPromptIndex(0);
      return;
    }
    const interval = setInterval(() => setPromptIndex(prev => (prev + 1) % prompts.length), PROMPT_CYCLE_INTERVAL);
    return () => clearInterval(interval);
  }, [isRecording, prompts.length]);

  return (
    <LayoutGroup>
      <div className='tw-flex tw-size-full tw-flex-1 tw-flex-col tw-relative'>
        <input {...getInputProps()} />

        {step === "input" && (
          <ViewLayout footer={<FooterMessage error={error} defaultMessage={t("upload_instructions")} />}>
            <SideBySideOmniboxLayout
              leftLabel={t("upload_a_recording")}
              rightLabel={t("record_your_voice")}
              leftContent={
                <div {...getRootProps({ className: cn("tw-size-full", isDragActive && "tw-bg-cream/50") })}>
                  <DiscButton
                    className='tw-ring-gray-200 group'
                    noiseOpacity={0}
                    colors={{ primary: "#EBE9E7", secondary: "#EBE9E7", accent: "#EBE9E7", highlight: "#EBE9E7" }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      openFileDialog();
                    }}
                  >
                    <DiscCenterButton
                      icon={<Plus16 />}
                      showShadow
                      className='tw-transition-all group-hover:[&>svg]:tw-opacity-70'
                    />
                  </DiscButton>
                </div>
              }
              rightContent={
                <m.div layoutId='isolate-disc' className='tw-size-full'>
                  <DiscButton
                    className='group'
                    image={{ src: imgRecordingBG, alt: "", sizes: DISC_SIZES }}
                    onClick={startRecording}
                  >
                    <DiscCenterButton
                      icon={<Mic16 />}
                      showShadow
                      className='tw-transition-all group-hover:[&>svg]:tw-opacity-70'
                    />
                  </DiscButton>
                </m.div>
              }
            />
          </ViewLayout>
        )}

        {(step === "recording" || step === "isolating") && (
          <CenteredLayout
            footer={
              isRecording ? (
                <RecordingPrompts prompts={prompts} promptIndex={promptIndex} />
              ) : (
                <p className='tw-text-black/60 tw-text-center tw-mx-auto tw-max-w-md tw-text-sm tw-leading-snug'>
                  {t("isolating_instructions")}
                </p>
              )
            }
          >
            <m.div layoutId='isolate-disc' className='tw-size-24 sm:tw-size-36 md:tw-size-52'>
              {isRecording ? (
                <DiscButton
                  className='tw-size-full group'
                  image={{ src: imgRecordingBG, alt: "", sizes: DISC_SIZES }}
                  onClick={stopRecording}
                >
                  <DiscCenterButton
                    icon={<StopIcon className='tw-text-black tw-size-4' />}
                    showShadow
                    className='tw-transition-all group-hover:[&>svg]:tw-opacity-70'
                  />
                </DiscButton>
              ) : (
                <DiscButton
                  className='tw-size-full tw-pointer-events-none'
                  image={{ src: imgIsolatingBG, alt: "", sizes: DISC_SIZES }}
                  onClick={() => {}}
                >
                  <DiscCenterButton
                    icon={<LoaderCircle className='tw-animate-spin tw-text-black' size={16} />}
                    showShadow
                  />
                </DiscButton>
              )}
            </m.div>
            <p className='tw-text-black/60 tw-text-sm'>
              {isRecording ? (
                <span className='tw-tabular-nums tw-text-black'>{formatTime(duration)}</span>
              ) : (
                t("isolating_audio")
              )}
            </p>
          </CenteredLayout>
        )}

        {step === "result" && <IsolationResultView voiceIsolateData={voiceIsolateData} />}
      </div>
    </LayoutGroup>
  );
}
